import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { ArrowLeft, Mail, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sales-pipeline-hub-24/artifacts/mmegsjtv_Ameenji.logo%20Black%20.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="Ameenji" className="h-16" />
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <h2 className="text-2xl font-heading font-bold text-slate-900">
              {sent ? "Check Your Email" : "Forgot Password"}
            </h2>
            <p className="text-sm text-slate-500 font-body mt-1">
              {sent
                ? "We've sent a password reset link to your email"
                : "Enter your email and we'll send you a reset link"}
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            {sent ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircle className="h-16 w-16 text-emerald-500" />
                </div>
                <p className="text-sm text-slate-600">
                  If an account exists for <strong>{email}</strong>, you'll receive an email with a link to reset your password. The link expires in 15 minutes.
                </p>
                <div className="pt-2 space-y-3">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setSent(false); setEmail(""); }}
                    data-testid="forgot-password-resend-btn"
                  >
                    <Mail size={16} className="mr-2" />
                    Try a different email
                  </Button>
                  <Link to="/login" className="block">
                    <Button
                      variant="ghost"
                      className="w-full text-[#155155]"
                      data-testid="forgot-password-back-login-btn"
                    >
                      <ArrowLeft size={16} className="mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="text"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 border-slate-300 focus:ring-2 focus:ring-[#155155]/20 focus:border-[#155155]"
                    data-testid="forgot-password-email-input"
                    required
                  />
                </div>

                {error && (
                  <div
                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    data-testid="forgot-password-error"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#155155] hover:bg-[#0E4040] text-white font-semibold shadow-sm transition-all active:scale-[0.98]"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail size={16} />
                      Send Reset Link
                    </span>
                  )}
                </Button>

                <Link to="/login" className="block">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-[#155155]"
                    data-testid="forgot-password-back-btn"
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6 font-body">
          Ameenji Rubber Limited &middot; Internal CRM System
        </p>
      </div>
    </div>
  );
}
